import React, { useState, useEffect, useCallback } from 'react';
import { Button, Upload, Radio, Form, message, Input, Progress, Spin, Typography, Modal, Checkbox, Tooltip } from 'antd';
import { UploadOutlined, DownloadOutlined, CloudUploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getAvailableCouriers, getAvailablePorts, getCustomers } from '../../services/userService';
import { getNextBatchNumber } from '../../services/batchService';
import ImportProgressHistory from './ImportProgressHistory';
import './OrderImportForm.css';

const { Title } = Typography;

const OrderImportForm = ({ onImportSuccess }) => {
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [pollTimeout, setPollTimeout] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [availablePorts, setAvailablePorts] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(true);
  const [loadingPorts, setLoadingPorts] = useState(true);
  const [selectedPort, setSelectedPort] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [suggestedBatchNumber, setSuggestedBatchNumber] = useState(null);
  const [currentPollInterval, setCurrentPollInterval] = useState(100); // Poll every 100ms for smoother updates
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [overwriteDuplicates, setOverwriteDuplicates] = useState(false);

  const fetchSuggestedBatchNumber = useCallback(async () => {
    try {
      const nextNumber = await getNextBatchNumber();
      setSuggestedBatchNumber(nextNumber);
    } catch (error) {
      console.error('Error fetching suggested batch number:', error);
    }
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setUserRole(user.role);
    }
  }, []);

  useEffect(() => {
    if (selectedCourier) {
      const courier = availableCouriers.find(c => c.code === selectedCourier);
      if (courier?.single_port) {
        setSelectedPort(courier.single_port.port_id);
        form.setFieldsValue({ port: courier.single_port.port_id });
      }
    }
  }, [selectedCourier, availableCouriers, form]);

  // Fetch customers if user is boss, manager, or staff
  useEffect(() => {
    if (userRole && ['BOSS', 'MANAGER', 'STAFF'].includes(userRole)) {
      const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
          const customersList = await getCustomers();
          setCustomers(customersList);
          // Auto-select first customer if there's only one
          if (customersList.length === 1) {
            setSelectedCustomer(customersList[0].id);
            form.setFieldsValue({ customer: customersList[0].id });
          }
        } catch (error) {
          console.error('Error fetching customers:', error);
          message.error('获取客户列表失败');
        } finally {
          setLoadingCustomers(false);
        }
      };
      fetchCustomers();
    }
  }, [userRole, form]);

  // Fetch couriers and ports once on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [couriers, ports] = await Promise.all([
          getAvailableCouriers(),
          getAvailablePorts()
        ]);
        setAvailableCouriers(couriers);
        setAvailablePorts(ports);
        
        // Auto-select courier if there's only one option
        if (couriers.length === 1) {
          setSelectedCourier(couriers[0].code);
          form.setFieldsValue({ courierService: couriers[0].code });
        }
      } catch (error) {
        message.error('获取可用快递公司和港口失败');
      } finally {
        setLoadingCouriers(false);
        setLoadingPorts(false);
      }
    };
    fetchData();
  }, []); // Empty dependency array - only run once on mount

  // Fetch batch number once on mount
  useEffect(() => {
    fetchSuggestedBatchNumber();
  }, []); // Empty dependency array - only run once on mount

  // Handle cleanup of poll timeout
  useEffect(() => {
    return () => {
      if (pollTimeout) {
        clearTimeout(pollTimeout);
      }
    };
  }, [pollTimeout]);

  const calculateNextPollInterval = (currentInterval, errorCount) => {
    const maxInterval = 500; // Cap at 500ms for more frequent updates
    const baseBackoffRate = 1.5;
    const errorPenalty = Math.min(errorCount * 500, 2000); // Additional delay for errors, max 2s
    
    return Math.min(
      currentInterval * baseBackoffRate + errorPenalty,
      maxInterval
    );
  };

  const checkProgress = async (importId) => {
    let retryCount = 0;
    const maxRetries = 3; // Number of immediate retries for transient errors

    while (retryCount < maxRetries) {
      try {
        const response = await axios.get(`/api/orders/import_progress/${importId}/`, {
          timeout: 5000 // 5 second timeout
        });

        const { 
          total, 
          processed, 
          percentage, 
          status, 
          error, 
          successful_count, 
          duplicate_count, 
          error_count,
          duplicate_orders,
          error_orders
        } = response.data;
        
        setImportProgress({
          total,
          processed,
          percentage,
          status,
          successful_count,
          duplicate_count,
          error_count,
          duplicate_orders
        });

        if (status === 'COMPLETED' || status === 'ERROR') {
          if (pollTimeout) {
            clearTimeout(pollTimeout);
          }
          setPollTimeout(null);
          setCurrentPollInterval(200); // Reset interval for next import
          setConsecutiveErrors(0); // Reset error count
          setUploading(false);
          
          if (status === 'COMPLETED') {
            // Refresh memcache for order information
            try {
              await axios.post('/api/orders/refresh_cache/');
            } catch (cacheError) {
              console.error('Error refreshing order cache:', cacheError);
              // Continue with import completion even if cache refresh fails
            }
            
            form.resetFields();
            setSelectedFileName(null);
            setImportResult({
              successful_count,
              duplicate_count,
              error_count,
              duplicate_orders: duplicate_orders || [],
              error_orders: error_orders || []
            });
          } else if (status === 'ERROR') {
            message.error(error || '导入过程中发生错误');
          }
          return;
        }

        // Success but not complete, continue polling
        setConsecutiveErrors(0); // Reset error count on success
        const nextInterval = calculateNextPollInterval(currentPollInterval, 0);
        setCurrentPollInterval(nextInterval);
        const timeout = setTimeout(() => checkProgress(importId), nextInterval);
        setPollTimeout(timeout);
        return;

      } catch (error) {
        console.error('Error checking progress:', error);
        retryCount++;
        
        if (retryCount === maxRetries) {
          // Only increment error count and show message after all retries fail
          setConsecutiveErrors(prev => prev + 1);
          
          if (pollTimeout) {
            clearTimeout(pollTimeout);
          }
          
          // Calculate next interval with error penalty
          const nextInterval = calculateNextPollInterval(
            currentPollInterval,
            consecutiveErrors + 1
          );
          setCurrentPollInterval(nextInterval);
          
          // Schedule next poll with increased interval
          const timeout = setTimeout(() => checkProgress(importId), nextInterval);
          setPollTimeout(timeout);
          
          // Show error message but don't stop polling
          message.error('检查导入进度时发生错误，将在稍后重试');
          return;
        }
        
        // Immediate retry for transient errors
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                      file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传 Excel 文件!');
        return false;
      }
      return false;
    },
    maxCount: 1,
    showUploadList: false,
    onChange: (info) => {
      if (info.fileList.length > 0) {
        setSelectedFileName(info.fileList[0].name);
      } else {
        setSelectedFileName(null);
      }
    }
  };

  const handleUpload = async (values) => {
    const { file, port, courierService, batchNumber, customer } = values;
    if (!file?.[0]) {
      message.error('请选择文件');
      return;
    }
    
    if (!courierService) {
      message.error('请选择快递');
      return;
    }

    if (!port) {
      message.error('请选择港口');
      return;
    }

    // Check if customer is required for boss/manager/staff
    if (['BOSS', 'MANAGER', 'STAFF'].includes(userRole) && !customer) {
      message.error('请选择客户');
      return;
    }

    const formData = new FormData();
    formData.append('file', file[0].originFileObj);
    formData.append('port', port);
    formData.append('courier_service', courierService);
    if (batchNumber) {
      formData.append('batch_number', batchNumber);
    }
    if (customer) {
      formData.append('customer_id', customer);
    }
    // overwrite_duplicates: when true, the backend will HARD-DELETE existing
    // orders with the same 订单号 before inserting the new ones. When false,
    // the existing soft-update / confirm flow is used.
    formData.append('overwrite_duplicates', overwriteDuplicates ? 'true' : 'false');

    setUploading(true);
    setImportProgress({ percentage: 0 });

    let finalFormData;
    try {
      // When overwriteDuplicates is on, skip the duplicate-check modal entirely
      // — the user has already confirmed they want to nuke any existing order
      // with the same 订单号.
      if (overwriteDuplicates) {
        finalFormData = formData;
      } else {
        // First check for duplicates
        const checkResponse = await axios.post('/api/orders/check_duplicates/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (checkResponse.data.duplicates?.length > 0) {
          const duplicateOrders = checkResponse.data.duplicates;
          finalFormData = await new Promise((resolve, reject) => {
            Modal.confirm({
              title: '发现重复订单',
              content: (
                <div>
                  <p>发现以下重复订单:</p>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', margin: '10px 0' }}>
                    {duplicateOrders.map((duplicate, index) => (
                      <div key={index} style={{ padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div><strong>订单号: {duplicate.order_number}</strong></div>
                        {duplicate.is_exact_duplicate ? (
                          <div style={{ color: '#52c41a' }}>所有字段完全匹配（此订单将被跳过不会重新导入）</div>
                        ) : (
                          <div>
                            <div style={{ color: '#faad14' }}>以下字段与现有订单不同:</div>
                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                              {duplicate.different_fields.map((field, idx) => {
                                const isUnchangeableField = ['寄件人姓名', '寄件人地址', '寄件人电话', '品名', '单位', '数量', '单价（元）'].includes(field);
                                return (
                                  <li key={idx} style={{ color: isUnchangeableField ? '#f5222d' : 'inherit' }}>
                                    {field}
                                    {isUnchangeableField && <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>（此字段不可更改，将跳过此订单）</span>}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p>字段有更改的订单将更新订单信息，为保正确派送请务必刷新订单页并重新打印面单贴单。是否继续？</p>
                </div>
              ),
              okText: '继续导入',
              cancelText: '取消',
              onOk: () => {
                formData.append('confirm_duplicates', 'true');
                resolve(formData);
              },
              onCancel: () => {
                setUploading(false);
                setImportProgress(null);
                reject(new Error('User cancelled'));
              },
            });
          });
        } else {
          finalFormData = formData;
        }
      }

      const response = await axios.post('/api/orders/import_orders/', finalFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const importId = response.data.import_id;
      // Start polling with initial interval
      const timeout = setTimeout(() => checkProgress(importId), currentPollInterval);
      setPollTimeout(timeout);

    } catch (error) {
      if (error.message !== 'User cancelled') {
        console.error('Error uploading file:', error.response?.data || error);
        message.error(error.response?.data?.error || '订单导入失败');
        setUploading(false);
        setImportProgress(null);
      }
    }
  };

  const formContent = (
    <Form
      form={form}
      onFinish={handleUpload}
      layout="horizontal"
      className="import-form"
    >
      <div className="form-row">
        <div className="form-item">
          <span className="form-label">文件：</span>
          <Form.Item
            name="file"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            style={{ marginBottom: 0 }}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} style={{ height: 32, width: 130, padding: '0 16px', fontSize: '13px' }}>
                选择文件
              </Button>
            </Upload>
          </Form.Item>
          {selectedFileName && (
            <span className="filename-text">
              {selectedFileName}
            </span>
          )}
        </div>

        <Form.Item name="port" style={{ display: 'none' }}>
          <Input type="hidden" />
        </Form.Item>

        <div className="form-item">
          <span className="form-label">快递：</span>
          <Form.Item
            name="courierService"
            style={{ marginBottom: 0 }}
          >
            {loadingCouriers ? (
              <Spin size="small" />
            ) : (
            <Radio.Group 
              size="small"
              value={selectedCourier}
              onChange={(e) => {
                setSelectedCourier(e.target.value);
                form.setFieldsValue({ courierService: e.target.value });
              }}
              className="courier-radio-group"
            >
              {availableCouriers.map(courier => (
                <Radio.Button 
                  key={courier.id} 
                  value={courier.code} 
                  style={{ fontSize: '13px' }}
                >
                  {courier.name}
                </Radio.Button>
              ))}
            </Radio.Group>
            )}
          </Form.Item>
        </div>

        {/* Customer selection for boss/manager/staff */}
        {userRole && ['BOSS', 'MANAGER', 'STAFF'].includes(userRole) && (
          <div className="form-item">
            <span className="form-label">客户：</span>
            <Form.Item
              name="customer"
              style={{ marginBottom: 0 }}
            >
              {loadingCustomers ? (
                <Spin size="small" />
              ) : (
                <Radio.Group
                  size="small"
                  value={selectedCustomer}
                  onChange={(e) => {
                    setSelectedCustomer(e.target.value);
                    form.setFieldsValue({ customer: e.target.value });
                  }}
                  className="courier-radio-group"
                >
                  {customers.map(customer => (
                    <Radio.Button
                      key={customer.id}
                      value={customer.id}
                      style={{ fontSize: '13px' }}
                    >
                      {customer.company_name || customer.username}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              )}
            </Form.Item>
          </div>
        )}

        {/* Overwrite duplicates toggle. When checked, existing orders with the
            same 订单号 are HARD-DELETED (not just is_deleted=True) and a fresh
            order record is inserted. Use with care. Placed on the same row as
            文件 / 快递 / 客户 so the form is a single, compact horizontal line. */}
        <div className="form-item">
          <Checkbox
            checked={overwriteDuplicates}
            onChange={(e) => setOverwriteDuplicates(e.target.checked)}
            style={{ fontSize: '13px' }}
          >
            <span style={{ fontWeight: overwriteDuplicates ? 600 : 400 }}>
              覆盖重复
            </span>
            <Tooltip
              title={
                <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
                  <div>勾选后：</div>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '18px' }}>
                    <li>已存在的同订单号订单将被<strong>硬删除</strong>（不只是标记删除）</li>
                    <li>然后用 Excel 中的数据重新创建一条新订单</li>
                    <li>历史的创建/更新记录会迁移到新订单</li>
                  </ul>
                  <div style={{ marginTop: '6px', color: '#ffccc7' }}>
                    警告：旧的运单号无法恢复，请谨慎使用。
                  </div>
                </div>
              }
            >
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
            </Tooltip>
          </Checkbox>
        </div>
      </div>

      <div className="form-row right-aligned">
        <div className="form-item" style={{ display: 'none' }}>
          <span className="form-label">批次：</span>
          <Form.Item name="batchNumber" style={{ marginBottom: 0 }}>
            <Input placeholder="请输入批次号" allowClear style={{ width: 130, height: 32, fontSize: '13px' }} />
          </Form.Item>
          <Button
            size="small"
            onClick={async () => {
              try {
                const number = suggestedBatchNumber || await getNextBatchNumber();
                if (!suggestedBatchNumber) {
                  setSuggestedBatchNumber(number);
                }
                form.setFieldsValue({ batchNumber: number });
              } catch (error) {
                message.error('获取批次号失败');
              }
            }}
            className="batch-suggestion-button"
          >
            使用批次号{suggestedBatchNumber || '...'}（可选）
          </Button>
        </div>
      </div>

      {(uploading || importResult) && (
        <div className={`import-results ${importResult ? 'visible' : ''}`}>
          <div className="import-summary">
            {uploading ? (
              <div>
                <Typography.Text>
                  已处理: <Typography.Text strong>{importProgress?.successful_count || 0}</Typography.Text> 成功
                  {importProgress?.duplicate_count > 0 && (
                    <> | <Typography.Text type="warning" strong>{importProgress.duplicate_count}</Typography.Text> 重复</>
                  )}
                  {importProgress?.error_count > 0 && (
                    <> | <Typography.Text type="danger" strong>{importProgress.error_count}</Typography.Text> 失败</>
                  )}
                </Typography.Text>
                <div style={{ fontSize: '13px', color: '#595959', marginTop: '6px', display: 'flex', gap: '16px' }}>
                  <span>批次进度: {Math.round((importProgress?.processed || 0) / (importProgress?.total || 1) * 100)}%</span>
                  <span>订单进度: {importProgress?.percentage || 0}%</span>
                </div>
              </div>
            ) : (
              <Typography.Text>
                导入完成: <Typography.Text strong>{importResult.successful_count}</Typography.Text> 成功
                {importResult.duplicate_count > 0 && (
                  <> | <Typography.Text type="warning" strong>{importResult.duplicate_count}</Typography.Text> 重复</>
                )}
                {importResult.error_count > 0 && (
                  <> | <Typography.Text type="danger" strong>{importResult.error_count}</Typography.Text> 失败</>
                )}
              </Typography.Text>
            )}
          </div>

          {!uploading && (
            <div className="result-details">
              {importResult.duplicate_orders?.length > 0 && (
                <div className="result-section">
                  <Typography.Text type="warning" strong>重复订单:</Typography.Text>
                  <ul className="order-list">
                    {importResult.duplicate_orders.map((orderNumber, index) => (
                      <li key={index}>{orderNumber}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResult.error_orders?.length > 0 && (
                <div className="result-section">
                  <Typography.Text type="danger" strong>导入失败订单:</Typography.Text>
                  <ul className="order-list">
                    {importResult.error_orders.map((order, index) => (
                      <li key={index}>
                        订单号 {order.order_number}: {order.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="import-button-row">
          <Button 
            type="primary" 
            htmlType="submit" 
            disabled={uploading}
            className="import-button"
          >
            <div className="import-button-text">
              <CloudUploadOutlined style={{ fontSize: '20px', marginRight: '8px' }} />
              {uploading ? (importProgress && importProgress.percentage === 100 ? '导入完成' : '正在导入') : '开始导入'}
            </div>
            {importProgress && (
              <>
                <div 
                  className="import-button-progress" 
                  style={{ 
                    width: `${importProgress.percentage}%`
                  }} 
                />
                <div className="import-button-percentage">
                  {importProgress.percentage}%
                </div>
                <div className="import-button-batch-progress" style={{ 
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: '3px',
                  backgroundColor: '#722ed1', // Purple color for batch progress
                  opacity: 0.3,
                  width: `${Math.round((importProgress.processed || 0) / (importProgress.total || 1) * 100)}%`,
                  transition: 'width 0.3s ease'
                }} />
              </>
            )}
          </Button>
        </div>

        <div style={{ marginTop: '20px', padding: '15px', fontSize: '14px', lineHeight: '1.8' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#1890ff' }}>注意事项:</div>
          <div style={{ marginBottom: '10px' }}>
            《订单导入模板》表格中的商家订单号、收件人姓名、收件人省、收件人市、收件人区、收件人街道、收件人电话、寄件人姓名、寄件人地址、寄件人电话、单件重量(KG)、数量为必填内容，其余可不填。
          </div>
          <div style={{ marginBottom: '10px' }}>
            <div>
              <span style={{ fontWeight: 'bold' }}>商家订单号：</span>至少10位数字以上，可加字母；
            </div>
            <div>
              <span style={{ fontWeight: 'bold' }}>单件重量(KG)：</span>重量导入要确保准确，单位一定要用公斤，不能用磅。导入的重量数据必须为包裹打包装箱后，复称的重量，不能大概估算物品的重量。国内顺丰快递对重量数据准确性有严格要求，如果频繁出现重量不符情况，会影响派送时效；
            </div>
            <div>
              <span style={{ fontWeight: 'bold' }}>数量：</span>填1，不能填真实数量或不填。
            </div>
          </div>
          <div style={{ color: '#cf1322' }}>
            重要提醒：为确保正确派送，第二次导入相同订单号的订单，请务必重新打印面单并贴单。
          </div>
        </div>
      </div>
    </Form>
  );

  return (
    <div className="import-container">
      <div className="import-header">
        <Button 
          type="primary"
          icon={<DownloadOutlined />}
          href="/templates/订单导入模板.xlsx" 
          download
          style={{ fontSize: '14px' }}
        >
          下载模板
        </Button>
        <Title level={1} style={{ margin: 0 }}>订单导入</Title>
        <div style={{ width: 85 }}></div>
      </div>

      {formContent}
      
      {/* Import Progress History - positioned in top right corner */}
      <ImportProgressHistory />
    </div>
  );
};

export default OrderImportForm;
